import { useEffect, useRef, useState } from "react";
import { PIMS_OPTIONS } from "../setup-flow-config";

interface Props {
    onSelect: (pimsValue: string) => void;
}

export default function PimsSelectionStep({ onSelect }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [focusedIndex, setFocusedIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const filtered = PIMS_OPTIONS.filter((p) => p.label.toLowerCase().includes(search.toLowerCase()));

    // Keep focusedIndex in bounds when filtering
    useEffect(() => {
        setFocusedIndex(0);
    }, [search]);

    // Scroll focused item into view
    useEffect(() => {
        if (!isOpen || !listRef.current) return;
        const items = listRef.current.children;
        if (items[focusedIndex]) {
            (items[focusedIndex] as HTMLElement).scrollIntoView({ block: "nearest" });
        }
    }, [focusedIndex, isOpen]);

    const handleSelect = (value: string) => {
        setIsOpen(false);
        onSelect(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === "Enter" || e.key === "ArrowDown") {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setFocusedIndex((i) => Math.min(i + 1, filtered.length - 1));
                break;
            case "ArrowUp":
                e.preventDefault();
                setFocusedIndex((i) => Math.max(i - 1, 0));
                break;
            case "Enter":
                e.preventDefault();
                if (filtered[focusedIndex]) {
                    handleSelect(filtered[focusedIndex].value);
                }
                break;
            case "Escape":
                e.preventDefault();
                setIsOpen(false);
                break;
        }
    };

    // Focus input when dropdown opens
    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen]);

    // Close on click outside
    const wrapperRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    return (
        <div className="flex w-full max-w-[520px] flex-col items-center gap-10">
            <div className="text-center">
                <h1 className="fraunces-soft text-4xl font-semibold leading-tight text-shadow-700">
                    What's your records system?
                </h1>
                <p className="mt-3 text-lg text-shadow-300">So I can set up your integration</p>
            </div>

            {/* Dropdown */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- onKeyDown only delegates bubbled keyboard events from the inner <button>/<input>; the wrapper is a positioning container, not itself a focusable control */}
            <div ref={wrapperRef} className="relative w-full" onKeyDown={handleKeyDown}>
                <button
                    type="button"
                    onClick={() => setIsOpen((o) => !o)}
                    className={`flex w-full items-center justify-between rounded-xl border bg-white px-4 py-3.5 text-left text-lg transition-colors focused-inset ${
                        isOpen ? "border-max-500" : "border-shadow-100"
                    }`}
                >
                    <span className="text-shadow-300">Select your PIMS</span>
                    <svg
                        className={`h-5 w-5 text-shadow-300 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {isOpen && (
                    <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-xl border border-shadow-100 bg-white shadow-lg">
                        {/* Search input */}
                        <div className="border-b border-shadow-50 px-3 py-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search..."
                                className="w-full bg-transparent px-2 py-1.5 text-base text-shadow-700 placeholder:text-shadow-300 rounded-md focused-inset"
                            />
                        </div>

                        {/* Options list */}
                        <div ref={listRef} className="max-h-[280px] overflow-y-auto py-1">
                            {filtered.length === 0 ? (
                                <p className="px-4 py-3 text-sm text-shadow-300">No results found</p>
                            ) : (
                                filtered.map((pims, i) => (
                                    <button
                                        key={pims.value}
                                        type="button"
                                        onClick={() => handleSelect(pims.value)}
                                        onMouseEnter={() => setFocusedIndex(i)}
                                        className={`w-full px-4 py-2.5 text-left text-base transition-colors focused-inset ${
                                            i === focusedIndex
                                                ? "bg-max-50 text-max-800"
                                                : "text-shadow-600 hover:bg-gray-50"
                                        }`}
                                    >
                                        {pims.label}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
